import { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import {
    ScrollText, Plus, Upload, Download, Search, X, Pencil, Trash2, CheckCircle2, XCircle,
    Loader2, ChevronLeft, ChevronRight, CheckSquare, Square, RefreshCw, Info,
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const PAGE_SIZE = 50;

const STATUS_STYLES = {
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
    superseded: 'bg-slate-100 text-slate-600 border-slate-200',
};
const SOURCE_LABELS = { gemini: 'AI generated', manual: 'Manual', imported: 'Imported' };

const EXPORT_HEADERS = ['Property', 'Noun/Modifier', 'Manufacturer', 'Condition', 'Status', 'Source', 'Version', 'Used', 'Updated'];

const EMPTY_FORM = { id: null, propertyName: '', nounModifier: '', manufacturerName: '', conditionText: '', status: 'approved' };

function useDebounced(value, delay = 350) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => { const t = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return debounced;
}

const formatDate = (value) => value ? new Date(value).toLocaleString() : '—';

/** Read an uploaded sheet into condition rows (header names are matched loosely). */
const readImportFile = async (file) => {
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    const pick = (row, ...names) => {
        const key = Object.keys(row).find(k => names.includes(k.trim().toLowerCase().replace(/[\s_/-]+/g, '')));
        return key ? String(row[key]).trim() : '';
    };
    return rows.map(row => ({
        propertyName: pick(row, 'property', 'propertyname'),
        nounModifier: pick(row, 'nounmodifier', 'nm'),
        manufacturerName: pick(row, 'manufacturer', 'manufacturername'),
        conditionText: pick(row, 'condition', 'conditiontext', 'rule'),
        status: pick(row, 'status').toLowerCase(),
    }));
};

function PropertyConditionsPage() {
    const { hasEditPermission } = useAuth();
    const canEdit = hasEditPermission('property_conditions');

    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({ total: 0, approved: 0, rejected: 0, superseded: 0, gemini: 0, manual: 0, imported: 0 });
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounced(search);
    const [statusFilter, setStatusFilter] = useState('');
    const [sourceFilter, setSourceFilter] = useState('');
    const [sort, setSort] = useState({ field: 'updated', dir: 'desc' });

    const [selected, setSelected] = useState(new Set());
    const [expanded, setExpanded] = useState(new Set());
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteIds, setDeleteIds] = useState(null);
    const [busy, setBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    const fetchRows = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const data = await apiClient.listPropertyConditions({
                search: debouncedSearch, status: statusFilter, source: sourceFilter,
                page, limit: PAGE_SIZE, sortField: sort.field, sortDir: sort.dir,
            });
            setRows(data.rows || []);
            setStats(data.stats || {});
            setPagination(data.pagination || { page, limit: PAGE_SIZE, total: 0, totalPages: 0 });
            setSelected(new Set());
        } catch (error) {
            toast.error(`Could not load property conditions: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, statusFilter, sourceFilter, sort]);

    useEffect(() => { fetchRows(1); }, [fetchRows]);

    const toggleSort = (field) => setSort(prev => ({ field, dir: prev.field === field && prev.dir === 'desc' ? 'asc' : 'desc' }));
    const toggleOne = (id) => setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    const toggleAll = () => setSelected(prev => prev.size === rows.length ? new Set() : new Set(rows.map(r => r.id)));
    const toggleExpanded = (id) => setExpanded(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });

    // ── Add / edit ───────────────────────────────────────────────────
    const saveForm = async () => {
        if (!form.propertyName.trim() || !form.conditionText.trim()) {
            toast.error('Property and condition are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                propertyName: form.propertyName, nounModifier: form.nounModifier,
                manufacturerName: form.manufacturerName, conditionText: form.conditionText, status: form.status,
            };
            if (form.id) await apiClient.updatePropertyCondition(form.id, payload);
            else await apiClient.createPropertyCondition(payload);
            toast.success(form.id ? 'Condition updated' : 'Condition added');
            setForm(null);
            fetchRows(form.id ? pagination.page : 1);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const setStatus = async (ids, status) => {
        setBusy(true);
        try {
            for (const id of ids) await apiClient.updatePropertyCondition(id, { status });
            toast.success(`${ids.length} condition(s) ${status}`);
            fetchRows(pagination.page);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async () => {
        setBusy(true);
        try {
            const result = await apiClient.deletePropertyConditions(deleteIds);
            toast.success(`Deleted ${result.deleted} condition(s)`);
            setDeleteIds(null);
            fetchRows(pagination.page);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setBusy(false);
        }
    };

    // ── Import / export ──────────────────────────────────────────────
    const handleImport = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        setBusy(true);
        try {
            const importRows = await readImportFile(file);
            if (importRows.length === 0) { toast.error('No rows found in the file'); return; }
            const result = await apiClient.importPropertyConditions(importRows);
            setImportResult(result);
            fetchRows(1);
        } catch (error) {
            toast.error(`Import failed: ${error.message}`);
        } finally {
            setBusy(false);
        }
    };

    const handleExport = async () => {
        setBusy(true);
        try {
            const data = await apiClient.listPropertyConditions({
                search: debouncedSearch, status: statusFilter, source: sourceFilter, limit: 0, sortField: sort.field, sortDir: sort.dir,
            });
            const sheetRows = (data.rows || []).map(r => [
                r.propertyName, r.nounModifier, r.manufacturerName, r.conditionText, r.status,
                SOURCE_LABELS[r.source] || r.source, r.version, r.usageCount, formatDate(r.updatedAt),
            ]);
            const ws = XLSX.utils.aoa_to_sheet([EXPORT_HEADERS, ...sheetRows]);
            ws['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 20 }, { wch: 80 }, { wch: 11 }, { wch: 13 }, { wch: 8 }, { wch: 8 }, { wch: 20 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Property Conditions');
            XLSX.writeFile(wb, `property-conditions-${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${sheetRows.length} condition(s)`);
        } catch (error) {
            toast.error(`Export failed: ${error.message}`);
        } finally {
            setBusy(false);
        }
    };

    // ── Render helpers ───────────────────────────────────────────────
    const statCards = [
        { label: 'Total', value: stats.total, onClick: () => { setStatusFilter(''); setSourceFilter(''); }, active: !statusFilter && !sourceFilter, tone: 'text-slate-800' },
        { label: 'Approved (used)', value: stats.approved, onClick: () => setStatusFilter('approved'), active: statusFilter === 'approved', tone: 'text-emerald-700' },
        { label: 'Rejected', value: stats.rejected, onClick: () => setStatusFilter('rejected'), active: statusFilter === 'rejected', tone: 'text-red-700' },
        { label: 'AI generated', value: stats.gemini, onClick: () => setSourceFilter('gemini'), active: sourceFilter === 'gemini', tone: 'text-blue-700' },
        { label: 'Manual', value: stats.manual, onClick: () => setSourceFilter('manual'), active: sourceFilter === 'manual', tone: 'text-violet-700' },
        { label: 'Imported', value: stats.imported, onClick: () => setSourceFilter('imported'), active: sourceFilter === 'imported', tone: 'text-amber-700' },
    ];

    const SortHeader = ({ field, children, className = '' }) => (
        <th className={`px-3 py-2 text-left font-semibold cursor-pointer select-none hover:text-slate-800 ${className}`} onClick={() => toggleSort(field)}>
            {children}{sort.field === field && <span className="ml-1 text-blue-600">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
        </th>
    );

    const selectedIds = [...selected];

    return (
        <div className="max-w-7xl mx-auto space-y-4">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImport} />

            {/* ══ HEADER ═══════════════════════════════════════ */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-200">
                        <ScrollText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-extrabold text-slate-900">Property Conditions</h1>
                        <p className="text-xs text-slate-500">Extraction rules used in Step 6 • {Number(stats.total || 0).toLocaleString()} rules • {Number(stats.approved || 0).toLocaleString()} approved</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <>
                            <button onClick={() => setForm({ ...EMPTY_FORM })}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition shadow-sm">
                                <Plus className="w-4 h-4" /> Add
                            </button>
                            <button onClick={() => fileInputRef.current?.click()} disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow-sm disabled:opacity-50">
                                <Upload className="w-4 h-4" /> Import
                            </button>
                        </>
                    )}
                    <button onClick={handleExport} disabled={busy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold rounded-xl transition shadow-sm disabled:opacity-50">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export
                    </button>
                </div>
            </div>

            {/* ══ HOW IT WORKS ═════════════════════════════════ */}
            <div className="flex gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900 leading-5">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p>
                    Each rule tells the AI how to extract one property. Only <strong>approved</strong> rules are used. The most specific rule wins:
                    a rule for the exact Noun/Modifier and manufacturer beats one for the Noun/Modifier only, which beats a general rule (blank Noun/Modifier).
                    When no approved rule exists, the AI writes one during the run; it is saved here when that product is saved to the database. <strong>Reject</strong> a bad rule to have the AI write a new one next time.
                </p>
            </div>

            {importResult && (
                <div className="flex items-center gap-4 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <span className="text-emerald-900">
                        <strong>Import complete:</strong> {importResult.inserted} added, {importResult.updated} updated, {importResult.skipped} skipped
                        {importResult.errors?.length > 0 && <span className="text-red-600"> • {importResult.errors.map(e => `row ${e.row}: ${e.reason}`).slice(0, 3).join('; ')}</span>}
                    </span>
                    <button onClick={() => setImportResult(null)} className="ml-auto text-emerald-500 hover:text-emerald-700"><X className="w-4 h-4" /></button>
                </div>
            )}

            {/* ══ STATS ════════════════════════════════════════ */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {statCards.map(card => (
                    <button key={card.label} onClick={card.onClick}
                        className={`rounded-xl border px-3 py-2.5 text-left bg-white transition ${card.active ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}>
                        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{card.label}</div>
                        <div className={`text-xl font-extrabold ${card.tone}`}>{Number(card.value || 0).toLocaleString()}</div>
                    </button>
                ))}
            </div>

            {/* ══ FILTERS + BULK ACTIONS ═══════════════════════ */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search property, Noun/Modifier, manufacturer or rule text…"
                        className="w-full pl-9 pr-8 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                    {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
                    <option value="">All statuses</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="superseded">Superseded</option>
                </select>
                <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm">
                    <option value="">All sources</option>
                    <option value="gemini">AI generated</option>
                    <option value="manual">Manual</option>
                    <option value="imported">Imported</option>
                </select>
                <button onClick={() => fetchRows(pagination.page)} className="p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-800" title="Refresh">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                {canEdit && selectedIds.length > 0 && (
                    <div className="flex items-center gap-1.5 ml-auto">
                        <span className="text-xs font-semibold text-slate-600">{selectedIds.length} selected</span>
                        <button onClick={() => setStatus(selectedIds, 'approved')} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50">Approve</button>
                        <button onClick={() => setStatus(selectedIds, 'rejected')} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-50">Reject</button>
                        <button onClick={() => setDeleteIds(selectedIds)} disabled={busy} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50">Delete</button>
                    </div>
                )}
            </div>

            {/* ══ TABLE ════════════════════════════════════════ */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                            <tr>
                                {canEdit && (
                                    <th className="px-3 py-2 w-8">
                                        <button onClick={toggleAll} className="text-slate-400 hover:text-blue-600">
                                            {rows.length > 0 && selected.size === rows.length ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                                        </button>
                                    </th>
                                )}
                                <SortHeader field="property">Property</SortHeader>
                                <SortHeader field="nounModifier">Noun/Modifier</SortHeader>
                                <SortHeader field="manufacturer">Manufacturer</SortHeader>
                                <th className="px-3 py-2 text-left font-semibold">Condition</th>
                                <SortHeader field="status">Status</SortHeader>
                                <SortHeader field="source">Source</SortHeader>
                                <SortHeader field="version" className="text-center">Ver.</SortHeader>
                                <SortHeader field="usage" className="text-center">Used</SortHeader>
                                <SortHeader field="updated">Updated</SortHeader>
                                {canEdit && <th className="px-3 py-2 text-center font-semibold">Actions</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading && rows.length === 0 && (
                                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">No property conditions match these filters.</td></tr>
                            )}
                            {rows.map(row => {
                                const isOpen = expanded.has(row.id);
                                return (
                                    <tr key={row.id} className={selected.has(row.id) ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}>
                                        {canEdit && (
                                            <td className="px-3 py-2 align-top">
                                                <button onClick={() => toggleOne(row.id)} className="text-slate-400 hover:text-blue-600">
                                                    {selected.has(row.id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                                                </button>
                                            </td>
                                        )}
                                        <td className="px-3 py-2 align-top"><code className="text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded">{row.propertyName}</code></td>
                                        <td className="px-3 py-2 align-top text-xs">
                                            {row.nounModifier
                                                ? <span className="inline-block px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 font-semibold border border-violet-100">{row.nounModifier}</span>
                                                : <span className="text-slate-400 italic">All</span>}
                                        </td>
                                        <td className="px-3 py-2 align-top text-xs text-slate-600">{row.manufacturerName || <span className="text-slate-400 italic">All</span>}</td>
                                        <td className="px-3 py-2 align-top text-xs text-slate-700 max-w-md">
                                            <button onClick={() => toggleExpanded(row.id)} className={`text-left ${isOpen ? 'whitespace-pre-wrap' : 'line-clamp-2'}`} title={isOpen ? 'Collapse' : 'Show full rule'}>
                                                {row.conditionText}
                                            </button>
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-bold border capitalize ${STATUS_STYLES[row.status] || STATUS_STYLES.superseded}`}>{row.status}</span>
                                        </td>
                                        <td className="px-3 py-2 align-top text-xs text-slate-600 whitespace-nowrap">{SOURCE_LABELS[row.source] || row.source}</td>
                                        <td className="px-3 py-2 align-top text-xs text-center font-mono text-slate-600">{row.version}</td>
                                        <td className="px-3 py-2 align-top text-xs text-center font-mono text-slate-600">{row.usageCount.toLocaleString()}</td>
                                        <td className="px-3 py-2 align-top text-[11px] text-slate-500 whitespace-nowrap">{formatDate(row.updatedAt)}</td>
                                        {canEdit && (
                                            <td className="px-3 py-2 align-top">
                                                <div className="flex items-center justify-center gap-0.5">
                                                    <button onClick={() => setForm({ ...row })} className="p-1 rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-50" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                                                    {row.status === 'approved'
                                                        ? <button onClick={() => setStatus([row.id], 'rejected')} disabled={busy} className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" title="Reject — the AI writes a new rule next time"><XCircle className="w-3.5 h-3.5" /></button>
                                                        : <button onClick={() => setStatus([row.id], 'approved')} disabled={busy} className="p-1 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50" title="Approve"><CheckCircle2 className="w-3.5 h-3.5" /></button>}
                                                    <button onClick={() => setDeleteIds([row.id])} className="p-1 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50/50 text-[11px] text-slate-500">
                        <span>
                            <span className="font-semibold text-slate-700">{(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-semibold text-slate-700">{pagination.total.toLocaleString()}</span>
                        </span>
                        <div className="flex items-center gap-1">
                            <button onClick={() => fetchRows(pagination.page - 1)} disabled={pagination.page <= 1} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                            <span className="px-2 font-semibold text-slate-700">Page {pagination.page} / {pagination.totalPages}</span>
                            <button onClick={() => fetchRows(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                    </div>
                )}
            </div>

            {/* ══ ADD / EDIT MODAL ═════════════════════════════ */}
            {form && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={() => !saving && setForm(null)}>
                    <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                            <h2 className="text-lg font-bold text-slate-900">{form.id ? 'Edit condition' : 'Add condition'}</h2>
                            <button onClick={() => setForm(null)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="space-y-3 px-6 py-5">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {[
                                    ['propertyName', 'Property *', 'e.g. TYPE'],
                                    ['nounModifier', 'Noun/Modifier', 'Blank = all'],
                                    ['manufacturerName', 'Manufacturer', 'Blank = all'],
                                ].map(([key, label, placeholder]) => (
                                    <label key={key} className="block text-xs font-semibold text-slate-600">
                                        {label}
                                        <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
                                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-200" />
                                    </label>
                                ))}
                            </div>
                            <label className="block text-xs font-semibold text-slate-600">
                                Condition (extraction rule) *
                                <textarea value={form.conditionText} onChange={e => setForm(f => ({ ...f, conditionText: e.target.value }))} rows={7}
                                    placeholder="What evidence qualifies, how to normalize the value, units, and when to leave it blank."
                                    className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
                            </label>
                            <label className="block text-xs font-semibold text-slate-600">
                                Status
                                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                                    className="mt-1 w-full sm:w-48 rounded-xl border border-slate-200 px-3 py-2 text-sm">
                                    <option value="approved">Approved — used in extraction</option>
                                    <option value="rejected">Rejected — not used</option>
                                    <option value="superseded">Superseded — not used</option>
                                </select>
                            </label>
                            {form.id && <p className="text-[11px] text-slate-500">Changing the rule text increases the version and marks it as Manual.</p>}
                        </div>
                        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-6 py-4 rounded-b-2xl">
                            <button onClick={() => setForm(null)} disabled={saving} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200">Cancel</button>
                            <button onClick={saveForm} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══ DELETE CONFIRM ═══════════════════════════════ */}
            {deleteIds && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl p-6 space-y-4">
                        <h2 className="text-lg font-bold text-slate-900">Delete {deleteIds.length} condition(s)?</h2>
                        <p className="text-sm text-slate-600">This cannot be undone. If a deleted property is needed again, the AI writes a new rule during the next run.</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setDeleteIds(null)} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
                            <button onClick={confirmDelete} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PropertyConditionsPage;
